import React, { useCallback, useEffect, useState } from "react";
import { useAuthInfo, useHostedPageUrls } from "@propelauth/react";
import { useAuthFrontendApis } from "@propelauth/frontend-apis-react";
import { VeysoMark } from "../components/VeysoMark.js";
import { enabledAuthMethods } from "./config.js";
// The enum, not the string: PropelAuth names twelve providers and a typo in a
// literal would only surface as a redirect to nowhere.
import { LoginState, SocialLoginProvider } from "@propelauth/frontend-apis";
import { nextStepFor } from "./nextStep.js";

/**
 * Our own sign-in and sign-up, rather than PropelAuth's hosted pages.
 *
 * The hosted pages work and are one line of code; what they cannot do is look
 * like the rest of the application. Landing on a differently-typeset page owned
 * by somebody else is the first thing a new account sees, and it reads as a
 * detour rather than as a step.
 *
 * PropelAuth is still doing the authentication — `@propelauth/frontend-apis`
 * posts to the same endpoints the hosted pages do, and the session it
 * establishes is the same session. Nothing here handles a password beyond
 * putting it in a request body, and no credential is stored on this side.
 *
 * What it does NOT own is every branch of the login state machine. There are
 * eight states and only one of them means "you are in". Two are handled here —
 * being signed in, and needing to confirm an email, which is every new account
 * and so not a rare path at all. The rest — enrolling in 2FA, a forced password
 * reset — are handed back to the hosted pages, because reimplementing them
 * badly is a worse failure than a change of scenery and they are rare.
 */
type Mode = "login" | "signup";

type Method = "link" | "password";

/**
 * Picks the confirmation step back up after a reload.
 *
 * Signing up and then closing the tab leaves a half-made account: PropelAuth
 * has the signup but the session is not usable, so the SDK reports nobody
 * signed in and this screen would show the login form. Typing the same
 * password again does eventually land on the confirmation step, but it asks
 * somebody to prove who they are in order to be told they still have an email
 * to open.
 *
 * So the login state is asked for once on arrival. Anyone genuinely signed out
 * gets `LoginRequired` and sees the ordinary form, which is every normal visit.
 *
 * The address is not recoverable here — the login state is a state, not a user
 * — so the step renders its copy without one.
 */
function useResumeConfirmation(enabled: boolean, onConfirmEmail: () => void) {
  const { fetchLoginState } = useAuthFrontendApis();
  const asked = React.useRef(false);

  useEffect(() => {
    if (!enabled || asked.current) return;
    asked.current = true;

    void (async () => {
      const response = await fetchLoginState();
      response.handle({
        success: (data) => {
          if (data.login_state === LoginState.EMAIL_NOT_CONFIRMED_YET) onConfirmEmail();
        },
        // Nothing to say. Not knowing the login state is exactly the situation
        // the ordinary sign-in form is for.
        unexpectedOrUnhandled: () => {},
      });
    })();
    // Once per mount, deliberately: this decides which screen to open on, and
    // re-running it would move somebody who pressed "Start again" back. The
    // ref above is what enforces that, not the dependency list.
  }, [enabled, fetchLoginState, onConfirmEmail]);
}

export const AuthScreen: React.FC = () => {
  const [mode, setMode] = useState<Mode>("login");
  /**
   * The address waiting on a confirmation, or null for the ordinary screen.
   *
   * Held here rather than in the form because confirming is a different step,
   * not a state of the form: the whole card changes, and the form that started
   * it is gone by then.
   */
  const [confirming, setConfirming] = useState<string | null>(null);
  /**
   * A step this screen cannot complete, described in our own words.
   *
   * Held rather than acted on: the card that explains it has a button, and
   * nothing about it happens on a timer.
   */
  const [elsewhere, setElsewhere] = useState<{ title: string; explanation: string } | null>(null);
  useResumeConfirmation(confirming === null && elsewhere === null, () => setConfirming(""));
  /**
   * Passwords first.
   *
   * A password signs somebody in on this page and leaves them on it. A link
   * cannot: the token has to be validated on the auth provider's domain, so the
   * email lands on a hosted "Finish logging in" page nobody asked to see. That
   * step is inherent to the method rather than a detour worth removing, which
   * makes the method itself the thing to change.
   *
   * The link is still here, and not only as a preference. A tenant can have
   * passwords switched off — this one did, and answering `/login` with
   * "Passwords are disabled" is the only way to find out, because nothing
   * reports the configuration to the browser. Shipping password-only against a
   * tenant like that would lock everybody out of an application whose login
   * screen looks like it is working.
   *
   * So the fallback below is a safety line, not a convenience.
   */
  // What this deployment says its tenant allows. Both unless told otherwise.
  const methods = enabledAuthMethods();
  const [method, setMethod] = useState<Method>(methods.includes("password") ? "password" : "link");
  // Set when the tenant tells us passwords are off, so the explanation appears
  // beside the form somebody is being moved to rather than the one they left.
  const [passwordsDisabled, setPasswordsDisabled] = useState<string | null>(null);

  // One heading, because with a link there is one act. "Sign in" and "Create
  // your account" are the same gesture here, and offering both would be asking
  // somebody to classify themselves before they can type an address.
  const title = method === "link" ? "Sign in to Veyso" : mode === "login" ? "Sign in to Veyso" : "Create your account";

  if (elsewhere !== null) {
    return (
      <div className="authsplit">
        <Pitch />
        <div className="authwrap">
          <div className="authcard">
            <VeysoMark size={28} />
            <ContinueElsewhere step={elsewhere} onBack={() => setElsewhere(null)} />
          </div>
        </div>
      </div>
    );
  }

  if (confirming !== null) {
    return (
      <div className="authsplit">
        <Pitch />
        <div className="authwrap">
          <div className="authcard">
            <VeysoMark size={28} />
            <ConfirmEmail email={confirming} onGiveUp={() => setConfirming(null)} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="authsplit">
      <Pitch />

      <div className="authwrap">
        <div className="authcard">
          <VeysoMark size={28} />
          <h1 className="auth__h1">{title}</h1>
          <p className="auth__sub">
            {method === "link"
              ? "New here or not, the same link gets you in."
              : mode === "login"
                ? "Turn a rough screen recording into a finished product demo."
                : "Your first three videos are on us."}
          </p>

          <GoogleButton mode={mode} />

          <div className="auth__or">
            <span>or</span>
          </div>

          {passwordsDisabled ? <p className="auth__hint">{passwordsDisabled}</p> : null}

          {method === "link" ? (
            <MagicLinkForm />
          ) : (
            <EmailForm
              mode={mode}
              onConfirmEmail={setConfirming}
              onContinueElsewhere={setElsewhere}
              onPasswordsDisabled={(message) => {
                // Moved rather than shown an error: the form they are looking
                // at cannot work on this tenant, and leaving them on it to read
                // about that is leaving them stuck.
                setPasswordsDisabled(message);
                setMethod("link");
              }}
            />
          )}

          {/* Both offered, because which one works is a property of the tenant
              and this screen cannot see it. PropelAuth exposes no endpoint that
              says "passwords are on"; the only way to find out is to try, and a
              screen that guessed wrong would strand somebody on a field their
              workspace refuses. So the other door is always one click away, and
              the error beside it names the setting if they pick the wrong one.

              The link is the default because it is the method that works on the
              most configurations: a tenant with passwords off still has this,
              and a tenant with both loses nothing by offering it first. */}
          {/* Only when there is something to switch to. A tenant that allows
              one method should not be offering the other — the button would
              lead to a form that answers "Not found", which is worse than no
              button, and there is no way for this page to find that out for
              itself. */}
          {methods.length > 1 ? (
            <button
              type="button"
              className="auth__link auth__method"
              onClick={() => setMethod(method === "link" ? "password" : "link")}
            >
              {method === "link" ? "Use a password instead" : "Email me a link instead"}
            </button>
          ) : null}

          {/* Only for passwords. A magic link has no separate sign-up to switch
              to, so offering the choice would be describing a distinction the
              method does not have. */}
          <p className="auth__switch" hidden={method === "link"}>
            {mode === "login" ? (
              <>
                No account yet?{" "}
                <button type="button" className="auth__link" onClick={() => setMode("signup")}>
                  Create one
                </button>
              </>
            ) : (
              <>
                Already have one?{" "}
                <button type="button" className="auth__link" onClick={() => setMode("login")}>
                  Sign in
                </button>
              </>
            )}
          </p>
        </div>
      </div>
    </div>
  );
};

/**
 * The brand side.
 *
 * Says what the product does rather than welcoming anybody: somebody at a sign
 * in screen either knows what this is and wants past it, or is about to create
 * an account and is still deciding. Three lines about the pipeline serve the
 * second and cost the first nothing.
 *
 * The figures are the ones already published on the marketing site, from the
 * same recording in the comparison there — not rounded, not improved. A number
 * invented here would be a number contradicting the homepage.
 *
 * Hidden below 60rem rather than stacked. On a phone the form is the whole
 * point and a panel above it is something to scroll past.
 */
const Pitch: React.FC = () => (
  <aside className="authpitch" aria-hidden="true">
    <div className="authpitch__inner">
      <p className="authpitch__eyebrow">Veyso</p>
      <h2 className="authpitch__h">
        Product videos.
        <br />
        Made in minutes.
      </h2>

      <ul className="authpitch__list">
        <li>
          <b>Cuts the dead air.</b>
          <span>The waiting, the mis-clicks, the part you said twice.</span>
        </li>
        <li>
          <b>Writes the script.</b>
          <span>From what is on screen, not a summary of it.</span>
        </li>
        <li>
          <b>Narrates it.</b>
          <span>A voice you choose, timed to the cut.</span>
        </li>
      </ul>

      <dl className="authpitch__facts">
        <div>
          <dt>Went in</dt>
          <dd className="tnum">1:24</dd>
        </div>
        <div>
          <dt>Came back</dt>
          <dd className="tnum">0:38</dd>
        </div>
        <div>
          <dt>Cost</dt>
          <dd className="tnum">Under $1</dd>
        </div>
      </dl>
    </div>
  </aside>
);

const GoogleButton: React.FC<{ mode: Mode }> = ({ mode }) => {
  const { loginWithSocialProvider } = useAuthFrontendApis();
  return (
    <button type="button" className="auth__social" onClick={() => loginWithSocialProvider(SocialLoginProvider.GOOGLE)}>
      <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
        <path
          fill="#4285F4"
          d="M45.1 24.5c0-1.6-.1-3.2-.4-4.7H24v8.9h11.8c-.5 2.8-2 5.1-4.4 6.7v5.6h7.1c4.2-3.8 6.6-9.5 6.6-16.5Z"
        />
        <path
          fill="#34A853"
          d="M24 46c6 0 11-2 14.6-5.4l-7.1-5.6c-2 1.3-4.5 2.1-7.5 2.1-5.8 0-10.7-3.9-12.4-9.1H4.3v5.7C7.9 41 15.4 46 24 46Z"
        />
        <path
          fill="#FBBC05"
          d="M11.6 28c-.4-1.3-.7-2.6-.7-4s.3-2.7.7-4v-5.7H4.3A22 22 0 0 0 2 24c0 3.5.9 6.9 2.3 9.7l7.3-5.7Z"
        />
        <path
          fill="#EA4335"
          d="M24 10.9c3.3 0 6.2 1.1 8.5 3.3l6.3-6.3C35 4.3 30 2 24 2 15.4 2 7.9 7 4.3 14.3l7.3 5.7c1.7-5.2 6.6-9.1 12.4-9.1Z"
        />
      </svg>
      {mode === "login" ? "Continue with Google" : "Sign up with Google"}
    </button>
  );
};

/**
 * A link in an email, instead of a password.
 *
 * Here because a tenant can have passwords switched off — ours does — and then
 * every password field on this screen is a dead end that answers "Password
 * login is not enabled." after somebody has typed into it. This is the door
 * that is actually open.
 *
 * There is no sign-up here, and that is the method rather than a shortcut. With
 * nothing to remember there is no difference between arriving for the first
 * time and arriving again: the same address, the same link, the same act.
 *
 * So `create_if_doesnt_exist` is always true, and not — as it was first written
 * — tied to whether the Sign in or Create account tab happened to be showing.
 * That version had a silent dead end in it. PropelAuth answers an unknown
 * address with success whether or not it creates the account, because saying
 * "no such user" would turn this into a way to find out who has one. With the
 * flag off, a new person would be told to check their email and no email would
 * ever come.
 *
 * A workspace that wants to keep people out does it in the dashboard, where the
 * setting belongs, rather than here where it only looks like it works.
 */
const MagicLinkForm: React.FC = () => {
  const { passwordlessLogin } = useAuthFrontendApis();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const response = await passwordlessLogin({ email, create_if_doesnt_exist: true });
      response.handle({
        success: () => setSent(true),
        passwordlessLoginDisabled: (problem) =>
          setError(firstMessage(problem) ?? "Email links are not enabled for this workspace."),
        badRequest: (problem) => setError(firstMessage(problem) ?? "Check the address and try again."),
        unexpectedOrUnhandled: (problem) => setError(firstMessage(problem) ?? "Something went wrong. Try again."),
      });
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <p className="auth__hint">
        Check <b>{email}</b> — the link signs you straight in. You can close this tab.
      </p>
    );
  }

  return (
    <form className="auth__form" onSubmit={(e) => void submit(e)}>
      <label className="auth__field">
        <span>Email</span>
        <input
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@company.com"
        />
      </label>

      {error ? <p className="auth__error">{error}</p> : null}

      <button type="submit" className="btn btn-primary auth__submit" disabled={busy}>
        {busy ? "Sending…" : "Email me a link"}
      </button>
      <p className="auth__hint">No password to remember. The link expires shortly after it arrives.</p>
    </form>
  );
};

/**
 * Waiting on the confirmation email, without leaving the application.
 *
 * This step used to be PropelAuth's hosted page, and it was the first thing
 * every new account saw: a page in somebody else's type, carrying their
 * default placeholder copy — "This is a header. It can be multiple lines." —
 * because that text is a tenant setting nobody had filled in. A stranger's
 * unfinished page, immediately after handing over a password.
 *
 * It does one thing the hosted page does not, which is the reason to own it
 * rather than merely restyle it: it notices. Confirming happens in a different
 * tab, or on a phone, and the hosted page has no idea — it sits there until
 * somebody reloads it. This asks PropelAuth what the login state is whenever
 * the tab comes back to the front, and on a slow tick besides, so coming back
 * from the email finds the app already signed in.
 */
const ConfirmEmail: React.FC<{ email: string; onGiveUp: () => void }> = ({ email, onGiveUp }) => {
  const { resendEmailConfirmation, fetchLoginState } = useAuthFrontendApis();
  const { refreshAuthInfo } = useAuthInfo();
  const { getLoginPageUrl } = useHostedPageUrls();

  /**
   * Where the emailed link actually goes.
   *
   * It goes to the auth provider, and it has to: the link carries a one-use
   * token that only they can redeem — there is no endpoint in either SDK that
   * takes it, so this cannot be brought onto our own domain however much we
   * would like to. Saying so in advance is the next best thing. Clicking a
   * link in an email and landing on a domain you have never heard of is the
   * shape of a phishing page, and somebody who has just signed up has no
   * reason yet to know that `auth.veyso.io` is us.
   */
  const authHost = (() => {
    try {
      return new URL(getLoginPageUrl()).host;
    } catch {
      return null;
    }
  })();

  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const check = useCallback(async () => {
    const response = await fetchLoginState();
    response.handle({
      success: (data) => {
        // `refreshAuthInfo` is what actually ends this screen: the Gate is
        // watching the SDK's auth info, not this component's state.
        if (data.login_state === LoginState.LOGGED_IN) void refreshAuthInfo();
      },
      // Silent on purpose. This runs on a timer, and a failed poll is not
      // something to put in front of somebody who is waiting on an email.
      unexpectedOrUnhandled: () => {},
    });
  }, [fetchLoginState, refreshAuthInfo]);

  useEffect(() => {
    // The tab coming back to the front is the strong signal — it usually means
    // they have just clicked the link somewhere else. The interval is the
    // fallback for confirming on a phone, where this tab never loses focus.
    const onVisible = () => {
      if (document.visibilityState === "visible") void check();
    };
    document.addEventListener("visibilitychange", onVisible);
    const timer = window.setInterval(() => void check(), 5000);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.clearInterval(timer);
    };
  }, [check]);

  const resend = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    setSent(false);
    const response = await resendEmailConfirmation();
    response.handle({
      success: () => setSent(true),
      rateLimited: () => setError("Already sent. Give it a few minutes before asking again."),
      // Not an error worth showing as one: they confirmed it, and the poll
      // below is about to notice and let them in.
      emailAlreadyConfirmed: () => void check(),
      unexpectedOrUnhandled: () => setError("Could not send it just now. Try again in a moment."),
    });
    setBusy(false);
  };

  return (
    <>
      <h1 className="auth__h1">Confirm your email</h1>
      <p className="auth__sub">
        {email ? (
          <>
            We sent a link to <strong>{email}</strong>. Open it and you are in.
          </>
        ) : (
          "We sent you a link. Open it and you are in."
        )}
      </p>

      {error ? <p className="auth__error">{error}</p> : null}
      {sent ? <p className="auth__hint">Sent. It should arrive within a minute.</p> : null}

      <button type="button" className="btn btn-primary auth__submit" disabled={busy} onClick={() => void resend()}>
        {busy ? "Sending…" : "Resend the email"}
      </button>

      <p className="auth__hint">
        Leave this tab open — it lets you in by itself once the address is confirmed, so there is nothing to come back
        and press.
        {authHost ? (
          <>
            {" "}
            The link opens on <strong>{authHost}</strong>, which runs sign-in for Veyso.
          </>
        ) : null}{" "}
        Check your spam folder if it has not arrived.
      </p>

      <p className="auth__switch">
        Wrong address?{" "}
        <button type="button" className="auth__link" onClick={onGiveUp}>
          Start again
        </button>
      </p>
    </>
  );
};

/**
 * A step that finishes on PropelAuth's pages, said in our words first.
 *
 * Two-step verification and forced password resets are screens of their own
 * and are rare enough that building them badly would be the worse trade. What
 * is not a good trade is moving somebody there silently, which is what a
 * 1200ms timer did: a page in this application's type was replaced by one in
 * somebody else's with no account of why, and the hosted page was carrying
 * PropelAuth's unfilled placeholder copy when it got there.
 *
 * So the explanation is ours and the decision is theirs. The button names the
 * domain, because a sign-in flow that changes address without warning is the
 * shape of a phishing page and people are right to be wary of it.
 */
const ContinueElsewhere: React.FC<{
  step: { title: string; explanation: string };
  onBack: () => void;
}> = ({ step, onBack }) => {
  const { getLoginPageUrl } = useHostedPageUrls();
  const url = getLoginPageUrl();
  const host = (() => {
    try {
      return new URL(url).host;
    } catch {
      return null;
    }
  })();

  return (
    <>
      <h1 className="auth__h1">{step.title}</h1>
      <p className="auth__sub">{step.explanation}</p>

      <button type="button" className="btn btn-primary auth__submit" onClick={() => (window.location.href = url)}>
        {"Continue"}
      </button>

      <p className="auth__hint">
        {host ? (
          <>
            This step finishes on <strong>{host}</strong>, which runs sign-in for Veyso. You will come back here
            afterwards.
          </>
        ) : (
          "This step finishes on the sign-in service that Veyso uses. You will come back here afterwards."
        )}
      </p>

      <p className="auth__switch">
        <button type="button" className="auth__link" onClick={onBack}>
          Back to sign in
        </button>
      </p>
    </>
  );
};

const EmailForm: React.FC<{
  mode: Mode;
  /** Called when the tenant refuses passwords, so the screen can offer the link. */
  onPasswordsDisabled: (message: string) => void;
  /** Called with the address that now needs confirming. */
  onConfirmEmail: (email: string) => void;
  /** Called with a step this screen cannot complete, to be explained rather than jumped to. */
  onContinueElsewhere: (step: { title: string; explanation: string }) => void;
}> = ({ mode, onPasswordsDisabled, onConfirmEmail, onContinueElsewhere }) => {
  const { emailPasswordLogin, signup, fetchLoginState } = useAuthFrontendApis();
  const { refreshAuthInfo } = useAuthInfo();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Only `Finished` means the session is live.
   *
   * Everything else — confirming an email, enrolling in 2FA, a forced password
   * reset — is a step this screen does not implement, and handing over is
   * right. Saying so first is the part that was missing: without it somebody
   * who has just typed a password into a page in this application's own type is
   * silently replaced by a generic one on a domain they have never seen, with
   * no account of why. A sentence and a moment is the difference between a
   * hand-off and a teleport.
   */
  /**
   * What the account actually needs next, asked rather than assumed.
   *
   * The login and signup calls each return a `login_state`, and it is
   * trustworthy when it says `Finished`. When it says anything else it can
   * disagree with the session that was just created: a signup that sent a
   * confirmation email answered `LoginRequired`, while `fetchLoginState()` a
   * moment later said `ConfirmEmailRequired` — which is why refreshing the
   * page showed the confirm screen and submitting the form showed "that did
   * not sign you in".
   *
   * PropelAuth documents `fetchLoginState` as the way to know what somebody
   * has to do next, so for everything except the happy path, ask it. One extra
   * request, only on the paths that were already going somewhere else.
   */
  const settledState = async (loginState: string): Promise<string> => {
    if (nextStepFor(loginState).kind === "signedIn") return loginState;

    let settled = loginState;
    const response = await fetchLoginState();
    response.handle({
      success: (data) => {
        settled = data.login_state;
      },
      // Keep what the first call said. It is not worse than it was.
      unexpectedOrUnhandled: () => {},
    });
    return settled;
  };

  const afterSuccess = async (loginState: string) => {
    const step = nextStepFor(await settledState(loginState));
    switch (step.kind) {
      case "signedIn":
        await refreshAuthInfo();
        return;
      case "confirmEmail":
        onConfirmEmail(email);
        return;
      case "retry":
        setError(step.message);
        return;
      case "continueElsewhere":
        // Told, not taken. Nothing navigates until somebody presses the button.
        onContinueElsewhere(step);
    }
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      if (mode === "login") {
        const response = await emailPasswordLogin({ email, password });
        response.handle({
          success: (data) => void afterSuccess(data.login_state),
          invalidCredentials: (problem) => setError(firstMessage(problem) ?? "That email and password do not match."),
          userAccountLocked: (problem) =>
            setError(firstMessage(problem) ?? "This account is locked. Check your email, or contact support."),
          userAccountDisabled: (problem) => setError(firstMessage(problem) ?? "This account has been disabled."),
          passwordLoginDisabled: (problem) =>
            onPasswordsDisabled(firstMessage(problem) ?? "Passwords are off for this workspace — use a link instead."),
          badRequest: (problem) => setError(firstMessage(problem) ?? "Check the email and password and try again."),
          // Anything PropelAuth adds later, and anything the network did.
          unexpectedOrUnhandled: () => setError("Something went wrong signing in. Try again."),
        });
      } else {
        const response = await signup({ email, password });
        response.handle({
          success: (data) => void afterSuccess(data.login_state),
          // `action_disabled` on a signup covers both "no signups" and "no
          // passwords", and the tenant that refuses one usually refuses the
          // other — so the link is offered rather than a dead end explained.
          signupDisabled: (problem) =>
            onPasswordsDisabled(firstMessage(problem) ?? "Sign-ups are closed for this workspace."),
          badRequest: (problem) => setError(firstMessage(problem) ?? "Check the details and try again."),
          unexpectedOrUnhandled: () => setError("Something went wrong creating the account. Try again."),
        });
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="auth__form" onSubmit={(e) => void submit(e)}>
      <label className="auth__field">
        <span>Email</span>
        <input
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@company.com"
        />
      </label>

      <label className="auth__field">
        <span>Password</span>
        <input
          type="password"
          // The browser is told which of the two this is, so it offers to save
          // a new password rather than autofilling an old one into a signup.
          autoComplete={mode === "login" ? "current-password" : "new-password"}
          required
          minLength={8}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder={mode === "signup" ? "At least 8 characters" : "••••••••"}
        />
      </label>

      {error ? <p className="auth__error">{error}</p> : null}

      <button type="submit" className="btn btn-primary auth__submit" disabled={busy}>
        {busy ? "One moment…" : mode === "login" ? "Sign in" : "Create account"}
      </button>

      {mode === "login" ? <ForgotPassword email={email} /> : null}
    </form>
  );
};

const ForgotPassword: React.FC<{ email: string }> = ({ email }) => {
  const { sendForgotPasswordEmail } = useAuthFrontendApis();
  const [sent, setSent] = useState(false);

  // Always reports as sent, whatever the answer. Telling somebody an address is
  // not registered turns this into a way to find out who has an account.
  const send = async () => {
    if (!email) return;
    const response = await sendForgotPasswordEmail({ email });
    response.handle({ success: () => setSent(true), unexpectedOrUnhandled: () => setSent(true) });
  };

  if (sent) return <p className="auth__hint">If that address has an account, a reset link is on its way.</p>;

  return (
    <button type="button" className="auth__link auth__forgot" onClick={() => void send()} disabled={!email}>
      Forgot your password?
    </button>
  );
};

/**
 * PropelAuth's own words, when it has any.
 *
 * Preferred over anything written here, and that preference was learned. A
 * tenant with passwords switched off answers `/signup` with "Password login is
 * not enabled." — precisely the problem, naming precisely the setting. This
 * screen replaced it with "Sign-ups are closed for this workspace", which is a
 * reasonable guess at what `action_disabled` means on a signup call and sent
 * somebody to the wrong switch in the dashboard for a day.
 *
 * The error codes are coarse: `action_disabled` covers every disabled thing
 * there is. The message beside it is specific, and it comes from the system
 * that knows. Ours is the fallback for when there is no message at all.
 */
function firstMessage(problem: unknown): string | null {
  const body = problem as { user_facing_error?: string; user_facing_errors?: Record<string, string> };
  if (body?.user_facing_error) return body.user_facing_error;
  const first = body?.user_facing_errors && Object.values(body.user_facing_errors)[0];
  return typeof first === "string" ? first : null;
}
